import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import { Sparkles, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/app/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-slate-950 text-neutral-100">
      {/* Left: editorial hero */}
      <div className="lg:col-span-2 border-r border-slate-800 p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-6">
            Vol 26 · The Affiliate Terminal
          </p>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] text-sky-100 tracking-tight">
            Autonomous income,
            <br />
            <span className="italic text-sky-400/90">served daily.</span>
          </h1>
          <p className="mt-6 text-neutral-400 text-base leading-relaxed max-w-md">
            Plug in your affiliate ID. Our GPT-5.6 engine detects the network,
            picks a profitable niche, writes the site, drops the links, and
            watches the checks roll in.
          </p>
        </div>
        <div className="relative mt-8 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          <p>◇ Amazon · ClickBank · CJ · ShareASale · Impact · Rakuten · Awin</p>
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:col-span-3 p-10 lg:p-24 flex items-center justify-center relative">
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6 fade-up">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
              — Session
            </p>
            <h2 className="font-serif text-3xl text-sky-100">Welcome back.</h2>
            <p className="text-sm text-neutral-400 mt-2">
              Sign in to manage your autonomous affiliate empire.
            </p>
          </div>

          {error && (
            <div data-testid="login-error" className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded-md">
              {error}
            </div>
          )}

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">
              Email
            </label>
            <input
              data-testid="input-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none transition placeholder:text-slate-600"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">
              Password
            </label>
            <div className="relative">
              <input
                data-testid="input-password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 pr-10 text-sky-50 outline-none transition placeholder:text-slate-600"
              />
              <button
                type="button"
                data-testid="btn-toggle-password"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-sky-300 transition"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Link to="/forgot-password" className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 hover:text-sky-300 mt-2 inline-block">
              Forgot password?
            </Link>
          </div>

          <button
            data-testid="btn-login"
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium tracking-wide rounded-md transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Signing in…" : (
              <>
                <Sparkles className="w-4 h-4" />
                Enter Terminal
              </>
            )}
          </button>

          <div className="border-t border-slate-800 pt-6 text-sm text-neutral-500">
            New here?{" "}
            <Link to="/signup" data-testid="link-signup" className="text-sky-300 hover:text-sky-200 border-b border-sky-400/40">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
